import { useLocation } from 'react-router-dom';
import { 
  Home, 
  Users, 
  CreditCard, 
  DollarSign, 
  AlertCircle, 
  Settings, 
  FileText,
  BarChart3,
  Plug,
  ListChecks,
  Target
} from 'lucide-react';

export interface BreadcrumbItem {
  label: string;
  href: string;
  icon?: React.ComponentType<{ className?: string }>;
}

interface RouteConfig {
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
}

const routeConfig: Record<string, RouteConfig> = {
  'dashboard': {
    label: 'Dashboard',
    icon: Home,
  },
  'customers': {
    label: 'Customers',
    icon: Users,
  },
  'transactions': {
    label: 'Transactions',
    icon: CreditCard,
  },
  'payments': {
    label: 'Payments',
    icon: DollarSign,
  },
  'collections': {
    label: 'Collections',
    icon: ListChecks,
  },
  'queue': {
    label: 'Collections Queue',
    icon: ListChecks,
  },
  'strategies': {
    label: 'Strategies',
    icon: Target,
  },
  'disputes': {
    label: 'Disputes',
    icon: AlertCircle,
  },
  'credit-scoring': {
    label: 'Credit Scoring',
    icon: BarChart3,
  },
  'settings': {
    label: 'Settings',
    icon: Settings,
  },
  'templates': {
    label: 'Templates',
    icon: FileText,
  },
  'edit': {
    label: 'Edit Template',
    icon: FileText,
  },
  'integrations': {
    label: 'Integrations',
    icon: Plug,
  },
};

export function useBreadcrumbs(): BreadcrumbItem[] {
  const location = useLocation();
  const pathSegments = location.pathname.split('/').filter(Boolean);

  // Always start with Dashboard
  const breadcrumbs: BreadcrumbItem[] = [
    {
      label: 'Dashboard',
      href: '/',
      icon: Home,
    },
  ];

  // Build breadcrumbs from path segments
  let currentPath = '';
  pathSegments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    
    const config = routeConfig[segment];
    
    // Handle dynamic segments (like IDs)
    if (!config && /^[0-9a-f-]+$/i.test(segment)) {
      // This looks like an ID, use a generic label
      const parentSegment = pathSegments[index - 1];
      const parentConfig = routeConfig[parentSegment];
      
      breadcrumbs.push({
        label: parentConfig?.label.slice(0, -1) || 'Detail', // Remove 's' from plural
        href: currentPath,
      });
    } else if (config) {
      breadcrumbs.push({
        label: config.label,
        href: currentPath,
        icon: config.icon,
      });
    } else {
      // Fallback: capitalize the segment
      breadcrumbs.push({
        label: segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' '),
        href: currentPath,
      });
    }
  });

  return breadcrumbs;
}
